import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Trash2, Plus, Eye, Users, Download, Pencil, Check, X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const KO_FINISHES = ["", "Winnaar", "Runner-up", "Laatste 4", "Laatste 8", "Laatste 16", "Laatste 32", "Laatste 64"];

const emptyEntry = {
  player_id: "",
  group_place: "",
  winners_ko_finish: "",
  consolation_ko_finish: "",
  highest_finish_bonus: false,
  count_100plus: 0,
  count_140plus: 0,
  count_180: 0,
  average: 0,
  legs_total: 0,
  legs_won: 0,
  matches_total: 0,
  matches_won: 0,
  best_legs: ["", "", ""],
};

const emptyMatch = {
  bracket_type: "winners",
  round_name: "Laatste 4",
  order: 0,
  player1_id: "",
  player2_id: "",
  player1_score: "",
  player2_score: "",
};

const ROUNDS = ["Laatste 64", "Laatste 32", "Laatste 16", "Laatste 8", "Laatste 4", "Finale"];

const TIER_LABEL = { "8": "t/m 8 inschr.", "16": "t/m 16 inschr.", "32": "t/m 32 inschr.", "64": "t/m 64 inschr." };

export default function AdminTournament() {
  const { id } = useParams();
  const [tour, setTour] = useState(null);
  const [tier, setTier] = useState("8");
  const [entries, setEntries] = useState([]);
  const [matches, setMatches] = useState([]);
  const [players, setPlayers] = useState([]);
  const [entry, setEntry] = useState(emptyEntry);
  const [match, setMatch] = useState(emptyMatch);
  const [editMatchId, setEditMatchId] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [editReg, setEditReg] = useState(null);

  const load = () => {
    api.get(`/tournaments/${id}`).then((r) => {
      setTour(r.data.tournament);
      setEntries(r.data.entries);
      setMatches(r.data.matches);
      setTier(r.data.tier || "8");
    });
    api.get("/players").then((r) => setPlayers(r.data));
    api.get(`/tournaments/${id}/registrations`).then((r) => setRegistrations(r.data)).catch(() => {});
  };
  useEffect(load, [id]);

  const delRegistration = async (rid) => {
    await api.delete(`/tournaments/${id}/registrations/${rid}`);
    toast.success("Inschrijving verwijderd");
    load();
  };

  const startEditReg = (r) => setEditReg({ id: r.id, first_name: r.first_name || "", last_name: r.last_name || "" });

  const saveEditReg = async () => {
    if (!editReg.first_name.trim() || !editReg.last_name.trim()) return toast.error("Vul voor- en achternaam in");
    try {
      await api.patch(`/tournaments/${id}/registrations/${editReg.id}`, {
        first_name: editReg.first_name.trim(),
        last_name: editReg.last_name.trim(),
      });
      toast.success("Naam bijgewerkt");
      setEditReg(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const exportRegistrations = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const header = "Naam,Achternaam,Email,Ingeschreven op\n";
    const rows = registrations
      .map((r) => [r.first_name, r.last_name, r.email, new Date(r.created_at).toLocaleString("nl-NL")].map(esc).join(","))
      .join("\n");
    const blob = new Blob([header + rows], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `inschrijvingen-${tour?.name || id}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const nameOf = (pid) => players.find((p) => p.id === pid)?.name || "—";

  const saveEntry = async (e) => {
    e.preventDefault();
    if (!entry.player_id) return toast.error("Selecteer een speler");
    try {
      await api.post(`/tournaments/${id}/entries`, {
        player_id: entry.player_id,
        group_place: entry.group_place ? Number(entry.group_place) : null,
        winners_ko_finish: entry.winners_ko_finish || null,
        consolation_ko_finish: entry.consolation_ko_finish || null,
        highest_finish_bonus: !!entry.highest_finish_bonus,
        count_100plus: Number(entry.count_100plus) || 0,
        count_140plus: Number(entry.count_140plus) || 0,
        count_180: Number(entry.count_180) || 0,
        average: Number(entry.average) || 0,
        legs_total: Number(entry.legs_total) || 0,
        legs_won: Number(entry.legs_won) || 0,
        matches_total: Number(entry.matches_total) || 0,
        matches_won: Number(entry.matches_won) || 0,
        best_legs: (entry.best_legs || []).map((l) => Number(l)).filter((l) => l > 0),
      });
      toast.success("Resultaat opgeslagen");
      setEntry(emptyEntry);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const editEntry = (en) => {
    const legs = en.best_legs || [];
    setEntry({
      player_id: en.player_id,
      group_place: en.group_place ?? "",
      winners_ko_finish: en.winners_ko_finish || "",
      consolation_ko_finish: en.consolation_ko_finish || "",
      highest_finish_bonus: !!en.highest_finish_bonus,
      count_100plus: en.count_100plus ?? 0,
      count_140plus: en.count_140plus ?? 0,
      count_180: en.count_180 ?? 0,
      average: en.average ?? 0,
      legs_total: en.legs_total ?? 0,
      legs_won: en.legs_won ?? 0,
      matches_total: en.matches_total ?? 0,
      matches_won: en.matches_won ?? 0,
      best_legs: [legs[0] ?? "", legs[1] ?? "", legs[2] ?? ""],
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const delEntry = async (eid) => {
    await api.delete(`/tournaments/${id}/entries/${eid}`);
    toast.success("Invoer verwijderd");
    load();
  };

  const saveMatch = async (e) => {
    e.preventDefault();
    const payload = {
      ...match,
      order: Number(match.order),
      player1_id: match.player1_id || null,
      player2_id: match.player2_id || null,
      player1_score: match.player1_score === "" ? null : Number(match.player1_score),
      player2_score: match.player2_score === "" ? null : Number(match.player2_score),
    };
    try {
      if (editMatchId) {
        await api.patch(`/tournaments/${id}/matches/${editMatchId}`, payload);
        toast.success("Wedstrijd bijgewerkt");
      } else {
        await api.post(`/tournaments/${id}/matches`, payload);
        toast.success("Wedstrijd toegevoegd");
      }
      setMatch(emptyMatch);
      setEditMatchId(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const editMatch = (m) => {
    setEditMatchId(m.id);
    setMatch({
      bracket_type: m.bracket_type,
      round_name: m.round_name,
      order: m.order,
      player1_id: m.player1_id || "",
      player2_id: m.player2_id || "",
      player1_score: m.player1_score ?? "",
      player2_score: m.player2_score ?? "",
    });
  };

  const delMatch = async (mid) => {
    await api.delete(`/tournaments/${id}/matches/${mid}`);
    toast.success("Wedstrijd verwijderd");
    load();
  };

  if (!tour) return <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 text-[#525252]">Laden…</div>;

  const inputCls = "w-full border border-[#E5E5E5] bg-white py-2 px-2 text-sm focus:outline-none focus:border-[#D4AF37]";
  const labelCls = "text-[10px] uppercase tracking-wider font-semibold text-[#525252] mb-1 block";

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-[#525252] hover:text-[#0A0A0A]">
          <ArrowLeft className="w-4 h-4" /> Dashboard
        </Link>
        <Link to={`/tournaments/${id}`} className="inline-flex items-center gap-1.5 text-sm font-semibold hover:text-[#A8882D]">
          <Eye className="w-4 h-4" /> Bekijk publieke pagina
        </Link>
      </div>

      <p className="font-mono text-sm text-[#D4AF37]">{tour.name === "Finale" ? "FINALE" : `RONDE ${String(tour.number).padStart(2, "0")}`} · BEHEER{tour.date ? ` · ${tour.date}` : ""}</p>
      <h1 className="font-heading text-4xl tracking-tighter font-black mb-10">{tour.name}</h1>

      {/* REGISTRATIONS */}
      <section className="mb-14">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
            <Users className="w-6 h-6 text-[#D4AF37]" /> Inschrijvingen ({registrations.length})
          </h2>
          {registrations.length > 0 && (
            <button
              onClick={exportRegistrations}
              data-testid="export-registrations-btn"
              className="inline-flex items-center gap-1.5 bg-transparent border border-[#0A0A0A] text-[#0A0A0A] px-4 py-2 text-sm font-semibold hover:bg-[#FDFBF7] transition-colors"
            >
              <Download className="w-4 h-4" /> Export CSV
            </button>
          )}
        </div>
        <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
          {registrations.length === 0 ? (
            <p className="p-8 text-center text-sm text-[#525252]">Nog geen inschrijvingen.</p>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-[#FDFBF7] border-b border-[#E5E5E5] text-[10px] uppercase tracking-wider text-[#525252]">
                  <th className="text-left py-2.5 px-4">Naam</th>
                  <th className="text-left py-2.5 px-4">Achternaam</th>
                  <th className="text-left py-2.5 px-4">E-mail</th>
                  <th className="text-left py-2.5 px-4">Ingeschreven</th>
                  <th className="py-2.5 px-4"></th>
                </tr>
              </thead>
              <tbody>
                {registrations.map((r) => (
                  <tr key={r.id} className="border-b border-[#E5E5E5] last:border-0 hover:bg-[#FDFBF7]" data-testid={`registration-row-${r.id}`}>
                    {editReg?.id === r.id ? (
                      <>
                        <td className="py-2.5 px-4">
                          <input value={editReg.first_name} onChange={(e) => setEditReg({ ...editReg, first_name: e.target.value })} data-testid={`edit-reg-firstname-${r.id}`} className="w-full border border-[#E5E5E5] py-1.5 px-2 text-sm focus:outline-none focus:border-[#D4AF37]" />
                        </td>
                        <td className="py-2.5 px-4">
                          <input value={editReg.last_name} onChange={(e) => setEditReg({ ...editReg, last_name: e.target.value })} data-testid={`edit-reg-lastname-${r.id}`} className="w-full border border-[#E5E5E5] py-1.5 px-2 text-sm focus:outline-none focus:border-[#D4AF37]" />
                        </td>
                        <td className="py-2.5 px-4 text-sm font-mono text-[#A0A0A0]">{r.email}</td>
                        <td className="py-2.5 px-4 text-xs text-[#525252]">{new Date(r.created_at).toLocaleDateString("nl-NL")}</td>
                        <td className="py-2.5 px-4 text-right whitespace-nowrap">
                          <button onClick={saveEditReg} data-testid={`save-reg-${r.id}`} className="text-[#16A34A] hover:text-[#15803D] mr-3"><Check className="w-4 h-4 inline" /></button>
                          <button onClick={() => setEditReg(null)} data-testid={`cancel-reg-${r.id}`} className="text-[#A0A0A0] hover:text-[#0A0A0A]"><X className="w-4 h-4 inline" /></button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="py-2.5 px-4 font-medium text-sm">{r.first_name}</td>
                        <td className="py-2.5 px-4 text-sm">{r.last_name}</td>
                        <td className="py-2.5 px-4 text-sm font-mono">{r.email}</td>
                        <td className="py-2.5 px-4 text-xs text-[#525252]">{new Date(r.created_at).toLocaleDateString("nl-NL")}</td>
                        <td className="py-2.5 px-4 text-right whitespace-nowrap">
                          <button onClick={() => startEditReg(r)} data-testid={`edit-registration-${r.id}`} className="text-[#525252] hover:text-[#D4AF37] mr-3"><Pencil className="w-4 h-4 inline" /></button>
                          <button onClick={() => delRegistration(r.id)} data-testid={`delete-registration-${r.id}`} className="text-[#A0A0A0] hover:text-[#DC2626]"><Trash2 className="w-4 h-4 inline" /></button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* GROUP STAGE + STATS */}
      <section className="mb-14">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <h2 className="font-heading text-2xl font-bold">Uitslag & statistieken</h2>
          <span className="text-xs font-semibold uppercase tracking-wider bg-[#0A0A0A] text-[#D4AF37] px-3 py-1.5">
            Puntenschaal: {TIER_LABEL[tier]} ({entries.length} deelnemers)
          </span>
        </div>
        <form onSubmit={saveEntry} className="bg-white border border-[#E5E5E5] p-5 mb-5 space-y-5" data-testid="entry-form">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            <div className="col-span-2">
              <label className={labelCls}>Speler</label>
              <select value={entry.player_id} onChange={(e) => setEntry({ ...entry, player_id: e.target.value })} data-testid="entry-player" className={inputCls}>
                <option value="">Selecteer speler…</option>
                {players.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Groepsplaats</label>
              <select value={entry.group_place} onChange={(e) => setEntry({ ...entry, group_place: e.target.value })} data-testid="entry-group-place" className={inputCls}>
                <option value="">—</option>
                {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}e</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Winnaars KO</label>
              <select value={entry.winners_ko_finish} onChange={(e) => setEntry({ ...entry, winners_ko_finish: e.target.value })} data-testid="entry-winners-ko" className={inputCls}>
                {KO_FINISHES.map((f) => <option key={f} value={f}>{f || "—"}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Verliezers KO</label>
              <select value={entry.consolation_ko_finish} onChange={(e) => setEntry({ ...entry, consolation_ko_finish: e.target.value })} data-testid="entry-consolation-ko" className={inputCls}>
                {KO_FINISHES.map((f) => <option key={f} value={f}>{f || "—"}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <Num label="100+" v={entry.count_100plus} on={(v) => setEntry({ ...entry, count_100plus: v })} cls={inputCls} lcls={labelCls} tid="entry-100plus" />
            <Num label="140+" v={entry.count_140plus} on={(v) => setEntry({ ...entry, count_140plus: v })} cls={inputCls} lcls={labelCls} tid="entry-140plus" />
            <Num label="180" v={entry.count_180} on={(v) => setEntry({ ...entry, count_180: v })} cls={inputCls} lcls={labelCls} tid="entry-180" />
            <Num label="Gemiddelde (TOT. AVG)" v={entry.average} on={(v) => setEntry({ ...entry, average: v })} cls={inputCls} lcls={labelCls} step="0.01" tid="entry-average" />
            <Num label="Legs gespeeld (legs totaal)" v={entry.legs_total} on={(v) => setEntry({ ...entry, legs_total: v })} cls={inputCls} lcls={labelCls} tid="entry-legs-total" />
            <Num label="Legs gewonnen (legs win)" v={entry.legs_won} on={(v) => setEntry({ ...entry, legs_won: v })} cls={inputCls} lcls={labelCls} tid="entry-legs-won" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 items-end">
            <Num label="Beste leg 1 (darts)" v={entry.best_legs[0]} on={(v) => setEntry({ ...entry, best_legs: [v, entry.best_legs[1], entry.best_legs[2]] })} cls={inputCls} lcls={labelCls} tid="entry-leg-1" allowEmpty />
            <Num label="Beste leg 2 (darts)" v={entry.best_legs[1]} on={(v) => setEntry({ ...entry, best_legs: [entry.best_legs[0], v, entry.best_legs[2]] })} cls={inputCls} lcls={labelCls} tid="entry-leg-2" allowEmpty />
            <Num label="Beste leg 3 (darts)" v={entry.best_legs[2]} on={(v) => setEntry({ ...entry, best_legs: [entry.best_legs[0], entry.best_legs[1], v] })} cls={inputCls} lcls={labelCls} tid="entry-leg-3" allowEmpty />
            <Num label="Wedstrijden gespeeld (matches)" v={entry.matches_total} on={(v) => setEntry({ ...entry, matches_total: v })} cls={inputCls} lcls={labelCls} tid="entry-matches-total" />
            <Num label="Gewonnen wedstrijden (matches win)" v={entry.matches_won} on={(v) => setEntry({ ...entry, matches_won: v })} cls={inputCls} lcls={labelCls} tid="entry-matches-won" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 items-end">
            <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
              <input type="checkbox" checked={entry.highest_finish_bonus} onChange={(e) => setEntry({ ...entry, highest_finish_bonus: e.target.checked })} data-testid="entry-highest-finish" className="w-4 h-4 accent-[#D4AF37]" />
              Hoogste finish (+1)
            </label>
            <button data-testid="save-entry-btn" className="inline-flex items-center justify-center gap-1.5 bg-[#0A0A0A] text-[#D4AF37] py-2 font-semibold hover:bg-[#1A1A1A] transition-colors">
              <Save className="w-4 h-4" /> Opslaan
            </button>
          </div>
        </form>

        <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-[#FDFBF7] border-b border-[#E5E5E5] text-[10px] uppercase tracking-wider text-[#525252]">
                <th className="text-left py-2.5 px-3">Speler</th>
                <th className="text-center py-2.5 px-2">Plaats</th>
                <th className="text-left py-2.5 px-2">W-KO</th>
                <th className="text-left py-2.5 px-2">V-KO</th>
                <th className="text-right py-2.5 px-2">100+</th>
                <th className="text-right py-2.5 px-2">140+</th>
                <th className="text-right py-2.5 px-2">180</th>
                <th className="text-right py-2.5 px-2">Gem.</th>
                <th className="text-right py-2.5 px-2 whitespace-nowrap">Tot. Games</th>
                <th className="text-right py-2.5 px-2 whitespace-nowrap">Games Won</th>
                <th className="text-right py-2.5 px-2 whitespace-nowrap">Tot. Legs</th>
                <th className="text-right py-2.5 px-2 whitespace-nowrap">Legs Won</th>
                <th className="text-center py-2.5 px-2">Bonus</th>
                <th className="text-right py-2.5 px-2">Punten</th>
                <th className="py-2.5 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 && <tr><td colSpan={15} className="py-8 text-center text-sm text-[#525252]">Nog geen invoer.</td></tr>}
              {entries.map((en) => (
                <tr key={en.id} className="border-b border-[#E5E5E5] last:border-0 hover:bg-[#FDFBF7]" data-testid={`entry-row-${en.id}`}>
                  <td className="py-2.5 px-3 font-medium text-sm">{nameOf(en.player_id)}</td>
                  <td className="py-2.5 px-2 text-center font-mono text-sm">{en.group_place ? `${en.group_place}e` : "—"}</td>
                  <td className="py-2.5 px-2 text-xs">{en.winners_ko_finish || "—"}</td>
                  <td className="py-2.5 px-2 text-xs">{en.consolation_ko_finish || "—"}</td>
                  <td className="py-2.5 px-2 text-right font-mono text-sm">{en.count_100plus}</td>
                  <td className="py-2.5 px-2 text-right font-mono text-sm">{en.count_140plus}</td>
                  <td className="py-2.5 px-2 text-right font-mono text-sm">{en.count_180}</td>
                  <td className="py-2.5 px-2 text-right font-mono text-sm">{en.average}</td>
                  <td className="py-2.5 px-2 text-right font-mono text-sm">{en.matches_total ?? 0}</td>
                  <td className="py-2.5 px-2 text-right font-mono text-sm">{en.matches_won ?? 0}</td>
                  <td className="py-2.5 px-2 text-right font-mono text-sm">{en.legs_total ?? 0}</td>
                  <td className="py-2.5 px-2 text-right font-mono text-sm">{en.legs_won ?? 0}</td>
                  <td className="py-2.5 px-2 text-center text-xs">{["+2", en.highest_finish_bonus ? "+1" : null].filter(Boolean).join(" ")}</td>
                  <td className="py-2.5 px-2 text-right font-mono text-sm font-bold text-[#A8882D]">{en.points}</td>
                  <td className="py-2.5 px-3 text-right whitespace-nowrap">
                    <button onClick={() => editEntry(en)} data-testid={`edit-entry-${en.id}`} className="text-[#525252] hover:text-[#D4AF37] mr-3 text-xs font-semibold">Bewerk</button>
                    <button onClick={() => delEntry(en.id)} data-testid={`delete-entry-${en.id}`} className="text-[#A0A0A0] hover:text-[#DC2626]"><Trash2 className="w-4 h-4 inline" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* KNOCKOUTS */}
      <section>
        <h2 className="font-heading text-2xl font-bold mb-4">Knock-out wedstrijden</h2>
        <form onSubmit={saveMatch} className="bg-white border border-[#E5E5E5] p-5 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-5" data-testid="match-form">
          <div>
            <label className={labelCls}>Schema</label>
            <select value={match.bracket_type} onChange={(e) => setMatch({ ...match, bracket_type: e.target.value })} data-testid="match-bracket" className={inputCls}>
              <option value="winners">Winnaars</option>
              <option value="consolation">Verliezers</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Ronde</label>
            <select value={match.round_name} onChange={(e) => setMatch({ ...match, round_name: e.target.value })} data-testid="match-round" className={inputCls}>
              {ROUNDS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Speler 1</label>
            <select value={match.player1_id} onChange={(e) => setMatch({ ...match, player1_id: e.target.value })} data-testid="match-player1" className={inputCls}>
              <option value="">—</option>
              {players.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <Num label="Score 1" v={match.player1_score} on={(v) => setMatch({ ...match, player1_score: v })} cls={inputCls} lcls={labelCls} tid="match-p1-score" allowEmpty />
          <div>
            <label className={labelCls}>Speler 2</label>
            <select value={match.player2_id} onChange={(e) => setMatch({ ...match, player2_id: e.target.value })} data-testid="match-player2" className={inputCls}>
              <option value="">—</option>
              {players.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <Num label="Score 2" v={match.player2_score} on={(v) => setMatch({ ...match, player2_score: v })} cls={inputCls} lcls={labelCls} tid="match-p2-score" allowEmpty />
          <div className="flex items-end">
            <button data-testid="save-match-btn" className="w-full inline-flex items-center justify-center gap-1.5 bg-[#0A0A0A] text-[#D4AF37] py-2 font-semibold hover:bg-[#1A1A1A] transition-colors">
              {editMatchId ? <Save className="w-4 h-4" /> : <Plus className="w-4 h-4" />} {editMatchId ? "Bijwerken" : "Toevoegen"}
            </button>
          </div>
        </form>

        <div className="bg-white border border-[#E5E5E5] divide-y divide-[#E5E5E5]">
          {matches.length === 0 && <p className="p-8 text-center text-sm text-[#525252]">Nog geen wedstrijden.</p>}
          {matches.map((m) => (
            <div key={m.id} className="flex items-center justify-between px-4 py-3" data-testid={`match-row-${m.id}`}>
              <div className="flex items-center gap-3 text-sm">
                <span className="font-mono text-[10px] uppercase px-2 py-0.5 bg-[#FDFBF7] border border-[#E5E5E5]">{m.bracket_type === "winners" ? "winnaars" : "verliezers"}</span>
                <span className="text-xs text-[#525252] w-28">{m.round_name}</span>
                <span className="font-medium">{nameOf(m.player1_id)}</span>
                <span className="font-mono font-bold">{m.player1_score ?? "–"} : {m.player2_score ?? "–"}</span>
                <span className="font-medium">{nameOf(m.player2_id)}</span>
              </div>
              <div className="whitespace-nowrap">
                <button onClick={() => editMatch(m)} data-testid={`edit-match-${m.id}`} className="text-[#525252] hover:text-[#D4AF37] mr-3 text-xs font-semibold">Bewerk</button>
                <button onClick={() => delMatch(m.id)} data-testid={`delete-match-${m.id}`} className="text-[#A0A0A0] hover:text-[#DC2626]"><Trash2 className="w-4 h-4 inline" /></button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

const Num = ({ label, v, on, cls, lcls, step, tid, allowEmpty }) => (
  <div>
    <label className={lcls}>{label}</label>
    <input
      type="number"
      step={step || "1"}
      value={v}
      data-testid={tid}
      onChange={(e) => on(allowEmpty && e.target.value === "" ? "" : e.target.value)}
      className={cls}
    />
  </div>
);
